import { useMemo, useState } from "react";
import { MapPin, Clock3, Shield } from "lucide-react";
import { useTranslation } from "react-i18next";
import trainings, { coachMeta } from "../../content/trainings";
import { getLocale } from "../../utils/content";
import styles from "./TrainingSchedule.module.scss";

const dayOrder = ["monday", "tuesday", "thursday", "friday"];

export default function TrainingSchedule() {
  const { t, i18n } = useTranslation();
  const locale = getLocale(i18n.language);
  const [activeId, setActiveId] = useState(trainings[0]?.id ?? null);

  const groupedTrainings = useMemo(
    () =>
      dayOrder.map((dayKey) => ({
        dayKey,
        sessions: trainings.filter((training) => training.dayKey === dayKey),
      })),
    []
  );

  const activeTraining =
    trainings.find((training) => training.id === activeId) ?? trainings[0];

  return (
    <section id="trainings" className={styles.trainingSection}>
      <div className={styles.heading}>
        <span className={styles.eyebrow}>{t("training_section_eyebrow")}</span>
        <h2>{t("training_section_title")}</h2>
        {/*<p>{t("training_section_intro")}</p>*/}
      </div>

      <div className={styles.calendarCard}>
        <div className={styles.legend}>
          <span>{t("training_hover_hint")}</span>
          <div className={styles.legendItems}>
            {Object.entries(coachMeta).map(([coachKey, coach]) => (
              <span key={coachKey} className={styles.legendItem}>
                <span
                  className={styles.legendSwatch}
                  style={{ backgroundColor: coach.color }}
                />
                {t("training_coach_label")}: {coach.name}
              </span>
            ))}
          </div>
        </div>

        <div className={styles.weekGrid}>
          {groupedTrainings.map(({ dayKey, sessions }) => (
            <article key={dayKey} className={styles.dayCard}>
              <div className={styles.dayHeader}>
                <h3>{t(`days.${dayKey}`)}</h3>
                <span>{sessions.length}</span>
              </div>

              <div className={styles.sessionList}>
                {sessions.map((session) => {
                  const coach = coachMeta[session.coachKey];
                  const isActive = activeTraining?.id === session.id;

                  return (
                    <button
                      key={session.id}
                      type="button"
                      className={styles.sessionChip}
                      style={{
                        "--session-color": coach.color,
                      }}
                      data-active={isActive}
                      onMouseEnter={() => setActiveId(session.id)}
                      onFocus={() => setActiveId(session.id)}
                      onClick={() => setActiveId(session.id)}
                    >
                      <strong>{coach.name}</strong>
                      <span>{session.time}</span>
                    </button>
                  );
                })}
              </div>
            </article>
          ))}
        </div>

        {activeTraining && (
          <aside className={styles.popoverCard}>
            <div
              className={styles.popoverAccent}
              style={{
                backgroundColor: coachMeta[activeTraining.coachKey].color,
              }}
            />
            <div className={styles.popoverBody}>
              <span className={styles.popoverBadge}>
                {t("days." + activeTraining.dayKey)}
              </span>
              <h3>
                {t("training_coach_label")}:{" "}
                {coachMeta[activeTraining.coachKey].name}
              </h3>
              <ul>
                <li>
                  <MapPin size={16} />
                  <span>
                    <strong>{t("training_location_label")}:</strong>{" "}
                    {activeTraining.location[locale]}
                  </span>
                </li>
                <li>
                  <Clock3 size={16} />
                  <span>
                    <strong>{t("training_time_label")}:</strong>{" "}
                    {activeTraining.time}
                  </span>
                </li>
                <li>
                  <Shield size={16} />
                  <span>
                    <strong>{t("training_coach_label")}:</strong>{" "}
                    {coachMeta[activeTraining.coachKey].name}
                  </span>
                </li>
              </ul>
            </div>
          </aside>
        )}
      </div>

      <div className={styles.listCard}>
        <h3>{t("training_list_title")}</h3>
        <div className={styles.trainingRows}>
          {groupedTrainings.map(({ dayKey, sessions }) => (
            <div key={dayKey} className={styles.trainingRow}>
              <div className={styles.trainingDay}>{t(`days.${dayKey}`)}</div>
              <div className={styles.trainingDetails}>
                {sessions.map((session) => (
                  <div key={session.id} className={styles.trainingItem}>
                    <span
                      className={styles.trainingItemMarker}
                      style={{
                        backgroundColor: coachMeta[session.coachKey].color,
                      }}
                    />
                    <div>
                      <strong>
                        {t("training_coach_label")}:{" "}
                        {coachMeta[session.coachKey].name}
                      </strong>
                      <p>{session.location[locale]}</p>
                    </div>
                    <span className={styles.trainingTime}>{session.time}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
